import { useState, ChangeEvent, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import Select from 'react-select'
import axios from 'axios';
import Swal from 'sweetalert2'
import DataTable from 'react-data-table-component';
import { API_URL, API_IMAGES, config, SITE_URL } from '../components/Include';
import { CustomLoader } from '../components/notify';


const News =()=> {
  const navigate = useNavigate();
  const staff    = useSelector((state:any) => state.rootReducer.staff);
  const category = useSelector((state:any) => state.rootReducer.category);

  const [loading, setLoading]         = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore]         = useState(false);
  const [page, setPage]               = useState(1);
  const [filterText, setFilterText]   = useState('');
  const [content, setContent]         = useState([] as any[]);
  const [filterContent, setFilterContent] = useState([] as any[]);

  const [search, setSearch] = useState({
    category: { label: '', value: '' },
    author:   { label: '', value: '' },
    staff:    { label: '', value: '' },
    status:   { label: '', value: '' },
    title: '',
    date_posted: '',
  });

  const [director, setDirector] = useState({
    isSN: false, isTitle: false, isPhoto: false, isCategory: false,
    isTrending: true, isSponsored: true, isMenu: true, isDate: true,
    isComment: true, isStatus: true, isAction: false, isView: false, isAuthor: true,
  });

  // ── filter loaded content by quick-search text ──────────────────────────────
  useEffect(() => {
    if (!filterText) {
      setFilterContent(content);
    } else {
      const q = filterText.toLowerCase();
      setFilterContent(content.filter((item: any) =>
        (item.title        || '').toLowerCase().includes(q) ||
        (item.category_name|| '').toLowerCase().includes(q) ||
        (item.status       || '').toLowerCase().includes(q) ||
        (item.staff_name   || '').toLowerCase().includes(q)
      ));
    }
  }, [filterText, content]);

  // ── fetch a page of news (append=true for load-more) ────────────────────────
  const fetchContent = useCallback(async (pg = 1, append = false) => {
    if (pg === 1) setLoading(true);
    else          setLoadingMore(true);

    const fd = new FormData();
    fd.append('page',      String(pg));
    fd.append('category',  search.category.value);
    fd.append('author',    search.author.value);
    fd.append('staff',     search.staff.value);
    fd.append('status',    search.status.value);
    fd.append('title',     search.title);
    fd.append('date_post', search.date_posted);

    try {
      const result = await axios.post(API_URL + '/news/display/all', fd, config);
      const data: any[] = Array.isArray(result.data.data) ? result.data.data : [];

      setContent(prev => append ? [...prev, ...data] : data);
      setHasMore(result.data.hasMore === true);
      setPage(pg);
    } catch (_) {
      // silently ignore
    } finally {
      if (pg === 1) setLoading(false);
      else          setLoadingMore(false);
    }
  }, [search]);

  useEffect(() => { fetchContent(1, false); }, []);

  const handleSearch = () => fetchContent(1, false);

  const handleReset = () => {
    setSearch({ category:{label:'',value:''}, author:{label:'',value:''},
                staff:{label:'',value:''}, status:{label:'',value:''},
                title:'', date_posted:'' });
    setFilterText('');
    fetchContent(1, false);
  };

  const handleLoadMore = () => fetchContent(page + 1, true);

  const handleSelect = (option: any, action: any) =>
    setSearch(prev => ({ ...prev, [action.name]: option }));

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setSearch(prev => ({ ...prev, [name]: value }));
  };

  const DeleteModal = (code: string) => {
    Swal.fire({
      title: 'Are you really sure?',
      text: "This is very dangerous, you shouldn't do it!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        return axios.get(API_URL + '/news/delete/' + code, config)
          .then(r => { if (r.data.status !== 200) throw new Error(r.statusText); return r.data; })
          .catch(e => Swal.showValidationMessage(`Request failed: ${e}`));
      },
      allowOutsideClick: () => !Swal.isLoading(),
      confirmButtonText: 'Yes, I am!',
    }).then((result: any) => {
      if (result.isConfirmed && result.value?.status === 200) {
        const rs = content.filter((item: any) => item.code !== code);
        setContent(rs);
        Swal.fire('Deleted!', 'Record has been deleted.', 'success');
      }
    });
  };

  // ── DataTable columns ────────────────────────────────────────────────────────
  const columns = [
    { name: 'SN', cell: (_: any, i: number) => i + 1, omit: director.isSN, width: '60px' },
    {
      name: 'Image',
      cell: (row: any) => (
        <img src={API_IMAGES + '/' + row.image_path} alt="" className="img-40"
             onError={(e) => { (e.target as HTMLImageElement).onerror = null; (e.target as HTMLImageElement).src = API_IMAGES + '/logo.png'; }} />
      ),
      omit: director.isPhoto, width: '100px',
    },
    {
      name: 'Title',
      cell: (row: any) => <a href={SITE_URL + '/post/' + row.slug} target="_blank" rel="noreferrer">{row.title}</a>,
      sortable: true, reorder: true, omit: director.isTitle,
    },
    { name: 'Category',     selector: (row: any) => row.category_name, sortable: true, reorder: true, omit: director.isCategory, width: '150px' },
    { name: 'Author',       selector: (row: any) => row.staff_name,    sortable: true, reorder: true, omit: director.isAuthor,   width: '150px' },
    { name: 'Is Trending',  selector: (row: any) => row.is_trending,   sortable: true, reorder: true, omit: director.isTrending, width: '110px' },
    { name: 'Is Sponsored', selector: (row: any) => row.is_sponsored,  sortable: true, reorder: true, omit: director.isSponsored,width: '110px' },
    { name: 'Is Menu',      selector: (row: any) => row.is_menu,       sortable: true, reorder: true, omit: director.isMenu,     width: '100px' },
    { name: 'Allow Comment',selector: (row: any) => row.allow_comment, sortable: true, reorder: true, omit: director.isComment,  width: '120px' },
    { name: 'Views',        selector: (row: any) => row.view_count,    sortable: true, reorder: true, omit: director.isView,     width: '80px'  },
    { name: 'Date Posted',  selector: (row: any) => row.created_at,    sortable: true, reorder: true, omit: director.isDate },
    {
      name: 'Status',
      cell: (row: any) => <span className={`badge ${row.status === 'Published' ? 'badge-secondary' : 'badge-danger'} me-1`}>{row.status}</span>,
      omit: director.isStatus, width: '100px',
    },
    {
      name: 'Action',
      cell: (row: any) => (
        <div>
          <a href={'/news/edit/' + row.code} className="btn btn-info-transparent btn-sm">
            <i className="fa fa-pencil" /> Modify
          </a>
          &nbsp;
          <button className="btn btn-danger-transparent btn-sm" onClick={() => DeleteModal(row.code)}>
            <i className="fa fa-trash" /> Delete
          </button>
        </div>
      ),
      omit: director.isAction, width: '200px',
    },
  ];

  return (
    <>
      <div className="page-header d-sm-flex d-block">
        <h4 className="page-title"><i className="fa fa-comment me-1" /> News</h4>
        <ol className="breadcrumb">
          <li className="breadcrumb-item"><a href="/">Home</a></li>
          <li className="breadcrumb-item active">News</li>
        </ol>
      </div>

      {/* Search card */}
      <div className="row row-sm">
        <div className="col-xl-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between">
              <div className="card-title text-primary">Search News</div>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-4">
                  <div className="form-group">
                    <label className="fw-bold mb-1">Search By Category</label>
                    <Select options={category?.map((n: any, i: number) => ({ value: n.code, key: i, label: n.category_name }))}
                            onChange={handleSelect} name="category" value={search.category} />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label className="fw-bold mb-1">Search By Author</label>
                    <Select options={staff?.map((n: any, i: number) => ({ value: n.code, key: i, label: n.staff_name }))}
                            onChange={handleSelect} name="author" value={search.author} />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label className="fw-bold mb-1">Search By Status</label>
                    <Select options={[{ value: 'Published', label: 'Published' }, { value: 'Draft', label: 'Draft' }]}
                            onChange={handleSelect} name="status" value={search.status} />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label className="fw-bold mb-1">Search By Date Posted</label>
                    <input type="date" className="form-control" value={search.date_posted} name="date_posted" onChange={handleChange} />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label className="fw-bold mb-1">Search By Title</label>
                    <input type="text" className="form-control" value={search.title} name="title" onChange={handleChange} />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label className="fw-bold mb-1">Search By Staff Posted</label>
                    <Select options={staff?.map((n: any, i: number) => ({ value: n.code, key: i, label: n.staff_name }))}
                            onChange={handleSelect} name="staff" value={search.staff} />
                  </div>
                </div>
              </div>
              <div className="card-footer">
                <div className="pull-right">
                  <button type="button" onClick={handleReset} className="btn btn-warning">
                    <i className="fa fa-refresh" /> Reset
                  </button>
                  &nbsp;
                  <button type="button" onClick={handleSearch} className="btn btn-secondary">
                    <i className="fa fa-search" /> Search
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Table card */}
      <div className="row row-sm">
        <div className="col-xl-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between">
              <div className="card-title">All News</div>
              <button type="button" className="btn btn-primary" onClick={() => navigate('/news/add')}>
                <i className="fa fa-plus" /> Add News
              </button>
            </div>
            <div className="card-body">
              <div className="row mb-3">
                <div className="col-md-8">
                  <div className="btn-group">
                    <button type="button" className="btn btn-secondary" data-bs-toggle="dropdown">
                      <i className="fa fa-filter fs-18" /> Column Visibility <i className="fa fa-arrow-down" />
                    </button>
                    <ul className="dropdown-menu" role="menu">
                      <li><a className={director.isSN       ? 'bg-secondary text-white' : ''} href="#!" onClick={() => setDirector(d => ({...d, isSN:      !d.isSN}))}>SN</a></li>
                      <li><a className={director.isPhoto    ? 'bg-secondary text-white' : ''} href="#!" onClick={() => setDirector(d => ({...d, isPhoto:   !d.isPhoto}))}>Photo</a></li>
                      <li><a className={director.isTitle    ? 'bg-secondary text-white' : ''} href="#!" onClick={() => setDirector(d => ({...d, isTitle:   !d.isTitle}))}>Title</a></li>
                      <li><a className={director.isCategory ? 'bg-secondary text-white' : ''} href="#!" onClick={() => setDirector(d => ({...d, isCategory:!d.isCategory}))}>Category</a></li>
                      <li><a className={director.isAuthor   ? 'bg-secondary text-white' : ''} href="#!" onClick={() => setDirector(d => ({...d, isAuthor:  !d.isAuthor}))}>Author</a></li>
                      <li><a className={director.isTrending ? 'bg-secondary text-white' : ''} href="#!" onClick={() => setDirector(d => ({...d, isTrending:!d.isTrending}))}>Is Trending</a></li>
                      <li><a className={director.isSponsored? 'bg-secondary text-white' : ''} href="#!" onClick={() => setDirector(d => ({...d, isSponsored:!d.isSponsored}))}>Is Sponsored</a></li>
                      <li><a className={director.isMenu     ? 'bg-secondary text-white' : ''} href="#!" onClick={() => setDirector(d => ({...d, isMenu:    !d.isMenu}))}>Is Menu</a></li>
                      <li><a className={director.isDate     ? 'bg-secondary text-white' : ''} href="#!" onClick={() => setDirector(d => ({...d, isDate:    !d.isDate}))}>Date Posted</a></li>
                      <li><a className={director.isComment  ? 'bg-secondary text-white' : ''} href="#!" onClick={() => setDirector(d => ({...d, isComment: !d.isComment}))}>Allow Comment</a></li>
                      <li><a className={director.isView     ? 'bg-secondary text-white' : ''} href="#!" onClick={() => setDirector(d => ({...d, isView:    !d.isView}))}>Total View</a></li>
                      <li><a className={director.isStatus   ? 'bg-secondary text-white' : ''} href="#!" onClick={() => setDirector(d => ({...d, isStatus:  !d.isStatus}))}>Status</a></li>
                      <li><a className={director.isAction   ? 'bg-secondary text-white' : ''} href="#!" onClick={() => setDirector(d => ({...d, isAction:  !d.isAction}))}>Action</a></li>
                      <div className="dropdown-divider" />
                      <li><a href="#!" onClick={() => setDirector({ isSN:false,isTitle:false,isPhoto:false,isCategory:false,isTrending:true,isSponsored:true,isMenu:true,isDate:true,isComment:true,isStatus:true,isAction:false,isView:false,isAuthor:true })}>Reset Column</a></li>
                    </ul>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="input-group">
                    <input type="text" className="form-control" placeholder="Quick search loaded rows…"
                           value={filterText} onChange={e => setFilterText(e.target.value)} />
                    <span className="input-group-text bg-default pointer" onClick={() => setFilterText('')}>
                      <i className="fa fa-close" />
                    </span>
                  </div>
                </div>
              </div>

              <DataTable
                columns={columns}
                data={filterContent}
                highlightOnHover
                striped
                responsive
                fixedHeader
                pagination={false}
                progressPending={loading}
                progressComponent={<CustomLoader />}
              />

              {/* Load More */}
              {hasMore && !loading && (
                <div className="text-center mt-3">
                  <button className="btn btn-outline-secondary px-5"
                          onClick={handleLoadMore}
                          disabled={loadingMore}>
                    {loadingMore
                      ? <><span className="spinner-border spinner-border-sm me-2" role="status" /> Loading…</>
                      : <><i className="fa fa-chevron-down me-1" /> Load More</>
                    }
                  </button>
                  <div className="text-muted small mt-1">{content.length} records loaded</div>
                </div>
              )}
              {!hasMore && content.length > 0 && !loading && (
                <div className="text-center text-muted small mt-3">{content.length} total records</div>
              )}

            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default News
